import * as Yup from 'yup';

import { AlertEnum, ButtonTypeEnum } from '../../enums';
import { ApiHelper, AppHelper } from '../../helpers';
import { Button, FileField, NumberField, TextField } from '../../components';
import { Form, Formik, FormikValues } from 'formik';
import { FunctionComponent, useState } from 'react';
import { useAlert, useAuthAxios, useErrorHandler, useForceReRender } from '../../hooks';

import { CandidaturaFormType } from '../../types';
import { useTranslation } from 'react-i18next';

const validImgFormats = ['image/png', 'image/jpg', 'image/jpeg'];

const candidaturaSchema = (t: Function) =>
  Yup.object().shape({
    descricao: Yup.string().required(t('validacao.campo_obrigatorio')),
    foto: Yup.object({
      id: Yup.number().notOneOf([0], t('validacao.campo_obrigatorio')).typeError(t('validacao.campo_obrigatorio')),
    }).test('isImg', t('validacao.formato_arquivo_invalido'), (value: any) => {
      return value.content_type && validImgFormats.includes(value.content_type.toLowerCase());
    }),
    foto_vice: Yup.object().test('isImg', t('validacao.formato_arquivo_invalido'), (value: any) => {
      return value.id === 0 || (value.content_type && validImgFormats.includes(value.content_type.toLowerCase()));
    }),
    nome_chapa: Yup.string().required(t('validacao.campo_obrigatorio')),
    numero_chapa: Yup.string().required(t('validacao.campo_obrigatorio')),
  });

const initialValues = {
  descricao: '',
  foto: {
    content_type: '',
    hash: '',
    id: 0,
    length: 0,
    nome: '',
  },
  foto_vice: {
    content_type: '',
    hash: '',
    id: 0,
    length: 0,
    nome: '',
  },
  nome_chapa: '',
  numero_chapa: '',
};

function preparaParaEnvio(values: FormikValues) {
  if (values.foto_vice?.id === 0) {
    values.foto_vice = null;
  }
  return values;
}

const CandidaturaForm: FunctionComponent<CandidaturaFormType> = ({ idEleicao, idCargo }) => {
  const { t } = useTranslation();
  const axios = useAuthAxios();
  const { createAlert } = useAlert();
  const errorHandler = useErrorHandler();
  const [showForm, setShowForm] = useState(false);
  const { forceReRender } = useForceReRender();

  return (
    <div className="container">
      {!showForm && (
        <div className="d-flex justify-content-center">
          <Button
            title={t('label.adicionar_candidatura')}
            onClick={() => {
              setShowForm(!showForm);
            }}
          />
        </div>
      )}
      {showForm && (
        <div>
          <Formik
            initialValues={initialValues}
            validationSchema={candidaturaSchema(t)}
            onSubmit={(values, { setSubmitting, resetForm }) => {
              setTimeout(() => {
                axios
                  .post(
                    ApiHelper.getUrl.apiSCE.candidaturas(idEleicao, idCargo),
                    preparaParaEnvio(AppHelper.cloneObject(values)),
                  )
                  .then((response) => {
                    resetForm();
                    forceReRender();
                    setShowForm(false);
                    createAlert(t('sucesso.adicionar_candidatura'), AlertEnum.SUCCESS);
                  })
                  .catch((error) => errorHandler(error, t('erro.adicionar_candidatura')))
                  .finally(() => {
                    setSubmitting(false);
                  });
              }, 400);
            }}
          >
            {(props) => (
              <Form>
                <div className="row">
                  <div className="col-md-6">
                    <NumberField
                      label={t('label.numero_chapa')}
                      name="numero_chapa"
                      formikProps={props}
                      required
                      maxLength={2}
                      addOn="#"
                    />
                  </div>
                  <div className="col-md-6">
                    <TextField label={t('label.nome_chapa')} name="nome_chapa" formikProps={props} required maxLength={100} />
                  </div>
                </div>
                <TextField label={t('label.descricao')} name="descricao" formikProps={props} required maxLength={200} />
                <div className="row">
                  <div className="col-md-6">
                    <FileField label={t('label.foto_candidato')} name="foto" formikProps={props} required />
                  </div>
                  <div className="col-md-6">
                    <FileField label={t('label.foto_vice_candidato')} name="foto_vice" formikProps={props} />
                  </div>
                </div>
                <div className="d-flex justify-content-center gap-5 pt-4">
                  <Button
                    title={t('label.cancelar')}
                    onClick={() => {
                      setShowForm(!showForm);
                    }}
                    disabled={props.isSubmitting || props.isValidating}
                    outline
                  />
                  <Button
                    title={t('label.adicionar')}
                    type={ButtonTypeEnum.SUBMIT}
                    disabled={props.isSubmitting || props.isValidating}
                  />
                </div>
              </Form>
            )}
          </Formik>{' '}
        </div>
      )}
    </div>
  );
};

export default CandidaturaForm;
